import { rcLogger, trackEvent } from '@readcloud/common';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
  ProgressBar,
} from '@readcloud/component-library';
import {
  Input,
  Button,
  Separator,
  Label,
  FormMessage,
  Alert,
  AlertDescription,
} from '@readcloud/ui';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { uniq } from 'ramda';
import React from 'react';
import { ModalFooter } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { AsyncSubject, BehaviorSubject, Subject } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';
import { useSelector } from 'react-redux';
import { readerSelectors } from '@root/libs/state/src';

/* 
  Target algorithm: (reference https://github.com/mozilla/pdf.js/blob/master/web/pdf_print_service.js)
  - Create a print container element that is printable, hide it from the dom (display: none)
  - Set some global styles to tell the browser how to print
  - start at page 0
  - render the page on a canvas with a white bg at 150DPI, with its native res (scale = 1 viewport) - use this transform: [PRINT_UNITS, 0, 0, PRINT_UNITS, 0, 0]
  - save the canvas to a blob and get a url for it, and render it to an img tag, add it to the container
  - render next page
*/

const printContainerClass = 'print-container';

const printContainerStyle: React.CSSProperties = {
  position: 'absolute',
  display: 'none',
  width: '100%',
  top: 0,
  left: 0,
};

const pageSizeOptions = {
  A2: {
    label: 'A2',
    height: 1684,
    width: 1191,
  },
  A3: {
    label: 'A3',
    height: 1191,
    width: 842,
  },
  A4: {
    label: 'A4',
    height: 842,
    width: 595,
  },
  A5: {
    label: 'A5',
    height: 595,
    width: 420,
  },
};

const scratchCanvas = document.createElement('canvas');
const PRINT_RESOLUTION = 150;
const PRINT_UNITS = PRINT_RESOLUTION / 72.0;
async function addPageToContainer(
  pdfDocument: PDFDocumentProxy,
  container: HTMLDivElement,
  pageIndex: number
) {
  const page = await pdfDocument.getPage(pageIndex + 1);
  const size = {
    ...pageSizeOptions.A4,
    height: pageSizeOptions.A4.height * (96 / 72) * 2,
    width: pageSizeOptions.A4.width * (96 / 72) * 2,
  };
  const context = scratchCanvas.getContext('2d', { alpha: false });
  /*   const scaleMultiplier = 150 / 72; */
  const unitViewport = page.getViewport({ scale: 1 });
  if (!context) return;
  context.save();
  context.fillStyle = 'rgb(255, 255, 255)';
  context.fillRect(0, 0, scratchCanvas.width, scratchCanvas.height);
  context.restore();
  scratchCanvas.width = Math.floor(unitViewport.width * PRINT_UNITS);
  scratchCanvas.height = Math.floor(unitViewport.height * PRINT_UNITS);
  const goalViewportScale = scratchCanvas.width / unitViewport.width;
  const goalViewport = page.getViewport({
    scale: goalViewportScale,
  });
  const task = page.render({
    canvasContext: context,
    viewport: goalViewport,
  });
  const verticalAspect = unitViewport.width / unitViewport.height;
  const horizontalAspect = unitViewport.height / unitViewport.width;
  const targetWidth =
    unitViewport.width < unitViewport.height
      ? size.height * verticalAspect
      : size.width;
  const targetHeight =
    unitViewport.width < unitViewport.height
      ? size.height
      : size.width * horizontalAspect;

  const promise = new Promise((res, rej) => {
    task.promise.then(() => {
      const url = scratchCanvas.toDataURL();
      const imgEl = document.createElement('img');
      imgEl.height = targetHeight;
      imgEl.width = targetWidth;
      imgEl.src = url;
      imgEl.style.margin = 'auto';
      imgEl.style.display = 'block';
      const imgContainer = document.createElement('div');

      imgContainer.style.margin = 'auto';
      imgContainer.style.pageBreakAfter = 'always';
      imgContainer.style.pageBreakInside = 'avoid';

      imgContainer.appendChild(imgEl);
      container.appendChild(imgContainer);
      imgEl.onload = res;
      imgEl.onerror = rej;
    }, rej);
  });

  return {
    promise,
    cancel: () => {
      try {
        task.cancel();
      } catch (e) {
        //
      }
    },
  };
}

const renderPages = (
  pdfDocument: PDFDocumentProxy,
  container: HTMLDivElement,
  printablePageIndexes: number[]
) => {
  const progress = new BehaviorSubject(0);
  const cancel = new Subject();
  const renderer = new Subject<number>();
  const finish = new AsyncSubject<'Cancelled' | 'Successful'>();
  const renderSub = renderer
    .pipe(
      takeUntil(cancel),
      concatMap(async (index) => {
        const currentPageIndex = printablePageIndexes[index];
        const task = await addPageToContainer(
          pdfDocument,
          container,
          printablePageIndexes[index]
        );
        if (!task) {
          return Promise.resolve();
        }
        const sub = cancel.subscribe(() => {
          task.cancel();
          sub.unsubscribe();
        });

        return task.promise
          .then(() => {
            sub.unsubscribe();
            if (index + 1 < printablePageIndexes.length) {
              renderer.next(index + 1);
              progress.next((index + 1) / printablePageIndexes.length);
            } else {
              renderer.complete();
            }
            return currentPageIndex;
          })
          .catch(() => {
            return currentPageIndex;
          });
      })
    )
    .subscribe(
      (pageIndex) => {
        rcLogger.debug(`Printing: Rendered page ${pageIndex}`);
      },
      undefined,
      () => {
        finish.next('Successful');
        finish.complete();
      }
    );

  cancel.subscribe(() => {
    renderSub.unsubscribe();
    finish.next('Cancelled');
    finish.complete();
  });

  renderer.next(0);
  return {
    progress,
    finish,
    renderer,
    cancel: () => {
      cancel.next({});
    },
  };
};

/**
 * Renders PDFPages to a div and only shows it when printing.
 *
 * It is VITAL to note that styles defined here need to stay local to the window props.
 * This means that you can't use a framework like emotion to define styles, because it will use the global window.
 *
 * style tags have been rendered within the window's head element, please keep it that way.
 */
export function PDFPrintContainer(props: {
  pdfDocument: PDFDocumentProxy;
  onBack: () => void;
  printablePages: number[];
  printableRanges: string;
  performPrint: () => Promise<void>;
  window: Window;
}) {
  const [elRef, setElRef] = React.useState<HTMLDivElement | null>(null);
  const [allowedPages, setAllowedPages] = React.useState<number[]>([]);
  const [startProcess, setStartProcess] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [userPrintPage, setUserPrintPages] = React.useState<string | undefined>(
    ''
  );
  const [renderingPage, setRenderingPage] = React.useState(1);
  const [finished, setFinished] = React.useState(false);
  const [inputInvalid, setInputInvalid] = React.useState(false);
  const taskRef = React.useRef<{ cancel: () => void }>();
  const readerData = useSelector(readerSelectors.getReaderData);

  //Use Effects
  React.useEffect(() => {
    if (props.printableRanges) {
      const validPages = buildPageArray(props.printableRanges);
      //console.log('Valid Page Range', validPages);
      setAllowedPages(validPages);
    }
  }, [props.printableRanges]);
  //Start Rendering Pages
  React.useEffect(() => {
    if (elRef && startProcess && userPrintPage) {
      const task = renderPages(
        props.pdfDocument,
        elRef,
        uniq(buildPageArray(userPrintPage).map((p) => Math.max(p - 1, 0)))
      );
      task.progress.subscribe(setProgress);
      task.finish.subscribe(() => {
        setFinished(true);
        taskRef.current = undefined;
      });
      task.renderer.subscribe((pageIndex) => {
        setRenderingPage(pageIndex + 1);
      });
      taskRef.current = task;
      return () => {
        task.cancel();
      };
    } else {
      return;
    }
  }, [props.pdfDocument, elRef, startProcess]);

  const buildPageArray = (pageRange: string) => {
    const pageArr = [];

    pageRange.split(',').forEach((pageNumber) => {
      //console.log(pageNumber);
      if (pageNumber.indexOf('-') >= 0) {
        const pageNumbers = pageNumber
          .split('-')
          .map((string) => parseInt(string));
        const minPage = Math.min.apply(Math, pageNumbers);
        const maxPage = Math.max.apply(Math, pageNumbers);
        for (let i = minPage; i <= maxPage; i++) {
          pageArr.push(i.toString());
        }
      } else {
        pageArr.push(pageNumber);
      }
    });

    return pageArr;
  };

  const validatePageInput = (pageInput: string) => {
    const convPageInput = buildPageArray(pageInput);
    let isValid = true;

    convPageInput.map((pNum) => {
      const pageExists = allowedPages.includes(pNum);
      if (!pageExists) {
        isValid = false;
      }
    });

    return isValid;
  };

  const cancel = React.useCallback(() => {
    if (taskRef.current) {
      taskRef.current.cancel();
    }
    props.onBack();
  }, []);

  const handleStartProcessing = () => {
    if (!validatePageInput(userPrintPage)) {
      setInputInvalid(true);
    } else {
      trackEvent('Webapp/PrintedBook', {
        bookTitle: readerData?.title,
        bookId: readerData.bookId,
        cloudId: readerData.cloudId,
        printableRanges: props.printableRanges,
        printablePages: props.printablePages,
        userPrintPage,
      });
      setInputInvalid(false);
      setStartProcess(true);
    }
  };

  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e) {
      if (e.target.value) {
        //if (!validatePageInput(e.target.value)) {
        // setInputInvalid(true);
        //} else {
        setUserPrintPages(e.target.value);
        //}
      } else {
        setUserPrintPages('');
      }
    }
  };

  const handlePrintAll = () => {
    setUserPrintPages(props.printableRanges);
  };

  const handlePrint = React.useCallback(() => {
    const tempTitle = document?.title;
    if (readerData?.title) {
      document.title = readerData?.title;
    }

    props.performPrint().then(() => (document.title = tempTitle));

    //props.onBack();
  }, [props.performPrint, props.onBack]);

  return (
    <>
      <Modal
        backdrop="static"
        isOpen={true}
        className="readcloud-ui-reset readcloud-ui"
      >
        <ModalHeader>
          <ModalTitle className="rc-scroll-m-20 sm:rc-text-xl rc-font-semibold rc-tracking-tight">
            Print PDF
          </ModalTitle>
        </ModalHeader>
        <Separator />
        <ModalBody>
          <div className="rc-flex rc-px-4 rc-flex-col rc-justify-center rc-gap-2">
            {!finished && progress > 0 ? (
              <ProgressBar
                className="rc-w-full"
                variant="determinate"
                now={progress * 100}
              />
            ) : null}
            {startProcess ? (
              <span>
                {!finished
                  ? `Rendering pages (${renderingPage} / ${props.printablePages.length})
              please wait...`
                  : `Finished Rendering!`}
              </span>
            ) : (
              <div className="rc-flex rc-flex-col rc-gap-4">
                <Alert>
                  <AlertDescription>
                    <h5>Printable Pages:</h5>
                    <div className="rc-max-h-40 rc-overflow-y-auto">
                      <ul className="rc-list-disc rc-pl-8">
                        {props.printableRanges.split(',').map((r, i) => (
                          <React.Fragment key={i}>
                            <li>
                              <span>{r}</span>
                            </li>
                          </React.Fragment>
                        ))}
                      </ul>
                    </div>
                  </AlertDescription>
                </Alert>
                <div className="rc-flex">
                  <div className="rc-flex rc-flex-col rc-gap-1.5 rc-w-full">
                    <Label>Pages</Label>
                    <div className="rc-flex rc-w-full">
                      <Input
                        onChange={handlePageInput}
                        placeholder="eg 1,2,4-6"
                        value={userPrintPage}
                      />
                      <Button className="rc-ml-2 max-sm:rc-text-xs" onClick={handlePrintAll}>
                        All
                      </Button>
                    </div>
                    <p className="rc-text-sm rc-mt-2 rc-text-slate-500">
                      Select which pages you would like to print.
                    </p>
                  </div>
                </div>
                {inputInvalid ? (
                  <span className="rc-text-sm rc-text-red-600">
                    Please only print the allowed pages.
                  </span>
                ) : undefined}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="rc-flex rc-w-full">
            {!elRef ? (
              <span>Loading...</span>
            ) : !startProcess ? (
              <div className="rc-flex rc-w-full rc-justify-between">
                  <Button variant="ghost" className='max-sm:rc-text-xs' onClick={cancel}>
                  Cancel
                </Button>
                  <Button className='max-sm:rc-text-xs' onClick={handleStartProcessing}>Select</Button>
              </div>
            ) : !finished ? (
              <Button variant="ghost" className='max-sm:rc-text-xs' onClick={cancel}>
                Cancel
              </Button>
            ) : (
              <div className="rc-flex rc-w-full rc-justify-between">
                <Button variant="ghost" className='max-sm:rc-text-xs' onClick={cancel}>
                  Close
                </Button>
                <Button className='max-sm:rc-text-xs' onClick={handlePrint}>Print</Button>
              </div>
            )}
          </div>
        </ModalFooter>
      </Modal>

      {/* PLEASE BE AWARE: styles are defined in this way because we want them to attach only to the window passed in as a prop.
          This allows you to render this component within a separate window or iframe to perform the printing in a more reliable way
        */}
      {createPortal(
        <style>
          {` 

          @media print {
            .${printContainerClass} {
              display: block !important;
            }
          }

          body {
              margin: 0px;
          }

          @page  {
              size: auto;   /* auto is the initial value */
              margin: 0mm;  /* this affects the margin in the printer settings */
          }

        @supports ((size:A4) and (size:1pt 1pt)) {
          @page {
              size: ${pageSizeOptions.A4.width * (96 / 72)}pt ${
            pageSizeOptions.A4.height * (96 / 72)
          }pt;
          }
        }
            `}
        </style>,

        props.window.document.head
      )}
      <div
        style={printContainerStyle}
        className={printContainerClass}
        ref={setElRef}
      />
    </>
  );
}
